import React, { useState, useEffect, useCallback } from 'react';

interface Screenshot {
    id: string | number;
    url: string;
    title: string;
    description?: string;
}

interface ImageLightboxProps {
    screenshots: Screenshot[];
    initialIndex: number;
    isOpen: boolean;
    onClose: () => void;
}

const ImageLightbox: React.FC<ImageLightboxProps> = ({
    screenshots,
    initialIndex,
    isOpen,
    onClose,
}) => {
    const [currentIndex, setCurrentIndex] = useState(initialIndex);

    useEffect(() => {
        setCurrentIndex(initialIndex);
    }, [initialIndex]);

    const handlePrevious = useCallback(() => {
        setCurrentIndex((prev) => (prev - 1 + screenshots.length) % screenshots.length);
    }, [screenshots.length]);

    const handleNext = useCallback(() => {
        setCurrentIndex((prev) => (prev + 1) % screenshots.length);
    }, [screenshots.length]);

    const handleKeyDown = useCallback(
        (e: KeyboardEvent) => {
            if (!isOpen) return;

            switch (e.key) {
                case 'Escape':
                    onClose();
                    break;
                case 'ArrowLeft':
                    handlePrevious();
                    break;
                case 'ArrowRight':
                    handleNext();
                    break;
            }
        },
        [isOpen, onClose, handlePrevious, handleNext]
    );

    useEffect(() => {
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [handleKeyDown]);

    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => {
            document.body.style.overflow = '';
        };
    }, [isOpen]);

    if (!isOpen || screenshots.length === 0) return null;

    const currentScreenshot = screenshots[currentIndex];

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-sm"
            onClick={onClose}
        >
            {/* Close Button */}
            <button
                onClick={onClose}
                className="absolute top-4 right-4 z-50 p-2 bg-gray-800/80 hover:bg-gray-700 rounded-full text-white transition-colors"
                aria-label="Close lightbox"
            >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
            </button>

            {/* Previous Button */}
            {screenshots.length > 1 && (
                <button
                    onClick={(e) => {
                        e.stopPropagation();
                        handlePrevious();
                    }}
                    className="absolute left-4 top-1/2 -translate-y-1/2 z-50 p-3 bg-gray-800/80 hover:bg-gray-700 rounded-full text-white transition-colors"
                    aria-label="Previous image"
                >
                    <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                </button>
            )}

            {/* Image Container */}
            <div
                className="relative max-w-[90vw] max-h-[85vh] flex flex-col items-center"
                onClick={(e) => e.stopPropagation()}
            >
                <img
                    src={currentScreenshot.url}
                    alt={currentScreenshot.title}
                    className="max-w-full max-h-[80vh] object-contain rounded-lg shadow-2xl"
                />

                {/* Title and Description */}
                {(currentScreenshot.title || currentScreenshot.description) && (
                    <div className="mt-4 text-center px-4">
                        {currentScreenshot.title && (
                            <h3 className="text-xl font-semibold text-white">{currentScreenshot.title}</h3>
                        )}
                        {currentScreenshot.description && (
                            <p className="text-gray-400 mt-1">{currentScreenshot.description}</p>
                        )}
                    </div>
                )}

                {/* Image Counter */}
                <div className="mt-4 text-gray-400 text-sm">
                    {currentIndex + 1} / {screenshots.length}
                </div>

                {/* Dot Indicators */}
                {screenshots.length > 1 && (
                    <div className="flex justify-center gap-2 mt-3">
                        {screenshots.map((_, index) => (
                            <button
                                key={index}
                                onClick={() => setCurrentIndex(index)}
                                className={`h-2 rounded-full transition-all duration-300 ${index === currentIndex
                                        ? 'w-8 bg-blue-500'
                                        : 'w-2 bg-gray-600 hover:bg-gray-500'
                                    }`}
                                aria-label={`Go to image ${index + 1}`}
                            />
                        ))}
                    </div>
                )}
            </div>

            {/* Next Button */}
            {screenshots.length > 1 && (
                <button
                    onClick={(e) => {
                        e.stopPropagation();
                        handleNext();
                    }}
                    className="absolute right-4 top-1/2 -translate-y-1/2 z-50 p-3 bg-gray-800/80 hover:bg-gray-700 rounded-full text-white transition-colors"
                    aria-label="Next image"
                >
                    <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                </button>
            )}
        </div>
    );
};

export default ImageLightbox;
